import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

export interface ShuffleArmState {
    /**
     * Whether shuffle is "armed" on a program/album detail page before playback
     * has started. Shared across the header Shuffle CTA and the track list so
     * that picking a track (or pressing Play) can start shuffled (EVE-13091).
     * Once a guide from the program is actually playing, the queue provider's
     * `isShuffled` is the source of truth instead.
     */
    isShuffleArmed: boolean;
    setShuffleArmed: (armed: boolean) => void;
}

export const ShuffleArmContext = createContext<ShuffleArmState>({
    isShuffleArmed: false,
    setShuffleArmed: () => undefined,
});

/**
 * Program-scoped holder for the "armed shuffle" intent. Reset whenever
 * `programId` changes so arming never leaks across programs — done via an
 * effect rather than a `key` on the subtree, so navigating between programs
 * resets only this flag and doesn't remount the whole program view (which would
 * reset tabs/dialogs/scroll too).
 */
export function ShuffleArmProvider({
    programId,
    children,
}: PropsWithChildren<{ programId?: string }>) {
    const [isShuffleArmed, setShuffleArmed] = useState(false);

    useEffect(() => {
        setShuffleArmed(false);
    }, [programId]);

    const value = useMemo(
        () => ({ isShuffleArmed, setShuffleArmed }),
        [isShuffleArmed]
    );
    return (
        <ShuffleArmContext.Provider value={value}>
            {children}
        </ShuffleArmContext.Provider>
    );
}

/** Access the program-scoped armed-shuffle state (see ShuffleArmProvider). */
export const useShuffleArm = () => useContext(ShuffleArmContext);
